import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Validators, FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { faTrashAlt, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { RulesService } from 'src/app/services/rules/rules.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-rule-detail',
  templateUrl: './rule-detail.component.html',
  styleUrls: ['./rule-detail.component.scss']
})
export class RuleDetailComponent implements OnInit {

  private _ruleId;
  private _ruleFromServer: any;

  public faTrashAlt = faTrashAlt;
  public faChevronLeft = faChevronLeft;
  public heading = 'Add New Rule';
  public isSubmitted = false;
  public queryOutput = '';
  public isErrorInQuery = false;

  constructor(private _route: ActivatedRoute, private _fb: FormBuilder, private _ruleService: RulesService, private _toastr: ToastrService, private _router: Router) {
    this._route.params.subscribe(param => {
      if (param['id']) {
        this._ruleId = param['id'];
        this.heading = 'Edit ';
      }
    });
  }

  public formRule: FormGroup;
  public equations: FormArray;
  public actions: FormArray;

  ngOnInit() {
    this._createForm();
    if (this._ruleId) {
      this._getRule();
    }
  }

  private _getRule() {
    this._ruleService.getById(this._ruleId).subscribe(res => {
      this._ruleFromServer = res;
      this.heading += res.name;
      this._patchFormdata(res);
    });
  }

  private _patchFormdata(data) {
    for (var i = 1; i < data['equations'].length; i++) {
      this.addEquation();
    }
    for (var i = 1; i < data['actions'].length; i++) {
      this.addAction();
    }
    this.formRule.reset(data);
  }

  private _createForm() {
    this.formRule = this._fb.group({
      id: null,
      name: ['', Validators.required],
      equations: this._fb.array([this._createEquation(true)]),
      actions: this._fb.array([this._createAction()]),
      query: ''
    });
  }

  private _createEquation(isFirst?: boolean): FormGroup {
    return this._fb.group({
      mainOperator: (isFirst)?'none':'and',
      bracketAtStart: 'none',
      key: 'deviceId',
      operator: '=',
      value: ['', Validators.required],
      bracketAtEnd: 'none'
    });
  }

  public addEquation(): void {
    this.equations = this.formRule.get('equations') as FormArray;
    this.equations.push(this._createEquation());
  }

  public removeEquation(index): void {
    this.equations = this.formRule.get('equations') as FormArray;
    this.equations.removeAt(index);
    if (!this.equations.length) {
      this.addEquation();
    }
    if(index === 0){
      this.equations.patchValue([{mainOperator: 'none'}]);
    }
  }
  
  private _createAction(): FormGroup {
    return this._fb.group({
      key: ['', Validators.required],
      value: ['', Validators.required]
    });
  }

  public addAction(): void {
    this.actions = this.formRule.get('actions') as FormArray;
    this.actions.push(this._createAction());
  }

  public removeAction(index): void {
    this.actions = this.formRule.get('actions') as FormArray;
    this.actions.removeAt(index);
    if (!this.actions.length) {
      this.addAction();
    }
  }

  private _clearFormArray(formArray: FormArray) {
    if (formArray) {
      while (formArray.length > 1) {
        formArray.removeAt(0)
      }
    }
  }

  public getQueryOutput() {
    this.queryOutput = '';
    var controlValue = this.formRule.controls['equations'].value;
    controlValue.forEach(element => {
      for (let ele in element) {
        if (element[ele] !== 'none' && Boolean(element[ele])) {
          this.queryOutput += (this.queryOutput) ? ' ' + element[ele] : element[ele];
        }
      }
    });
    this._validateQueryOutput();
  }

  private _validateQueryOutput() {
    var openBracesOccurences = this._getOccurenceIndexes(this.queryOutput, '(');
    var closeBracesOccurences = this._getOccurenceIndexes(this.queryOutput, ')');
    if (openBracesOccurences.length !== closeBracesOccurences.length) {
      this.isErrorInQuery = true;
    } else {
      if (openBracesOccurences.length > 0) {
        var isError = false;
        for (let i = 0; i < openBracesOccurences.length; i++) {
          if (i > 0 && openBracesOccurences[i] < closeBracesOccurences[i - 1]) {
            isError = true;
          }
        }
        if (isError) {
          this.isErrorInQuery = true;
        } else {
          this.isErrorInQuery = false;
        }
      } else {
        this.isErrorInQuery = false;
      }

    }
  }

  private _getOccurenceIndexes(source, find) {
    if (!source) {
      return [];
    }
    if (!find) {
      return source.split('').map(function (_, i) { return i; });
    }
    var result = [];
    var i = 0;
    while (i < source.length) {
      if (source.substring(i, i + find.length) == find) {
        result.push(i);
        i += find.length;
      } else {
        i++;
      }
    }
    return result;
  }

  public onSubmit() {
    this.isSubmitted = true;
    this.getQueryOutput();
    if (this.formRule.invalid || this.isErrorInQuery) { return }
    this.formRule.controls['query'].setValue(this.queryOutput);
    if (this._ruleId) {
      this._ruleService.update(this.formRule.value).subscribe(res => {
        this._ruleFromServer = res;
        this._toastr.success('Rule updated successfully.', '', { timeOut: 3000 });
      });
    } else {
      this._ruleService.save(this.formRule.value).subscribe(res => {
        this._toastr.success('Rule added successfully.', '', { timeOut: 3000 });
        this.onReset();
      });
    }
  }

  public onReset() {
    this.formRule.reset(this._createForm());
    this.isSubmitted = false;
    this.isErrorInQuery = false;
    this.queryOutput = '';
    if (this._ruleFromServer) {
      this._patchFormdata(this._ruleFromServer);
    }
  }

  public onBack() {
    this._router.navigate(['/app/', 'rules']);
  }

  get f() { return this.formRule.controls }

}